import { useEffect, useMemo, useState, type CSSProperties } from 'react';

/**
 * Structured editor for a JSON Schema document — the schema flavor used by
 * workflow input/output contracts. Models one row per property under an
 * `{ type: "object" }` root (recursing into nested objects and array
 * `items`), with type / required / description on each row. Includes a
 * raw-JSON escape hatch for power users (oneOf, $ref, anyOf, etc.).
 *
 * Modeled on `JsonObjectBuilder` — same look-and-feel, same row-per-key
 * grid, same Builder/Raw toggle. The internal AST is intentionally light:
 * we only model what the row UI can render. Unrecognized keywords on a
 * property survive a round-trip through `extra` (they're spread back on
 * serialize) so editing a schema authored by a power user in raw mode
 * doesn't silently drop their `format`, `minimum`, `pattern`, etc.
 */

// ─── AST ────────────────────────────────────────────────────────────────────

type SchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'null' | 'object' | 'array' | 'any';

interface ISchemaNode {
  type: SchemaType;
  description?: string;
  // object
  fields?: IField[];
  /** Whether the object permits keys beyond those listed in `fields`. Default true. */
  additionalProperties?: boolean;
  // array
  items?: ISchemaNode;
  /** Untouched JSON-Schema keywords we don't model (format, minLength, etc). */
  extra?: Record<string, unknown>;
}

interface IField {
  name: string;
  required: boolean;
  schema: ISchemaNode;
}

const primitiveTypes: SchemaType[] = [
  'string',
  'number',
  'integer',
  'boolean',
  'null',
  'object',
  'array',
  'any',
];

function defaultNode(t: SchemaType): ISchemaNode {
  if (t === 'object') return { type: 'object', fields: [], additionalProperties: false };
  if (t === 'array') return { type: 'array', items: { type: 'string' } };
  return { type: t };
}

// ─── Parse / serialize ──────────────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

const knownKeys = new Set([
  'type',
  'description',
  'properties',
  'required',
  'items',
  'additionalProperties',
  '$schema',
]);

function parseNode(raw: unknown): ISchemaNode {
  if (!isPlainObject(raw)) return { type: 'any' };
  const t = raw.type;
  const description = typeof raw.description === 'string' ? raw.description : undefined;
  const extra = collectExtras(raw);

  if (t === 'object' || (t === undefined && isPlainObject(raw.properties))) {
    const props = isPlainObject(raw.properties) ? raw.properties : {};
    const required = new Set(
      Array.isArray(raw.required)
        ? raw.required.filter((v): v is string => typeof v === 'string')
        : [],
    );
    const fields: IField[] = Object.entries(props).map(([name, sub]) => ({
      name,
      required: required.has(name),
      schema: parseNode(sub),
    }));
    // JSON Schema's `additionalProperties` defaults to `true`. We only
    // model the boolean form here; complex schema forms survive via `extra`.
    const additionalProperties =
      typeof raw.additionalProperties === 'boolean' ? raw.additionalProperties : true;
    return { type: 'object', description, fields, additionalProperties, extra };
  }
  if (t === 'array') {
    const items = isPlainObject(raw.items) ? parseNode(raw.items) : { type: 'any' as SchemaType };
    return { type: 'array', description, items, extra };
  }
  if (typeof t === 'string' && primitiveTypes.includes(t as SchemaType)) {
    return { type: t as SchemaType, description, extra };
  }
  return { type: 'any', description, extra };
}

function collectExtras(raw: Record<string, unknown>): Record<string, unknown> | undefined {
  const extras: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (!knownKeys.has(k)) extras[k] = v;
  }
  return Object.keys(extras).length > 0 ? extras : undefined;
}

function serializeNode(n: ISchemaNode): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (n.extra) Object.assign(out, n.extra);
  if (n.type === 'any') {
    // No `type` at all = matches anything. Still emit description if set.
    if (n.description) out.description = n.description;
    return out;
  }
  out.type = n.type;
  if (n.description) out.description = n.description;
  if (n.type === 'object') {
    const properties: Record<string, unknown> = {};
    const required: string[] = [];
    for (const f of n.fields ?? []) {
      if (!f.name) continue;
      properties[f.name] = serializeNode(f.schema);
      if (f.required) required.push(f.name);
    }
    out.properties = properties;
    if (required.length > 0) out.required = required;
    // Only emit when the user explicitly opts out — default JSON Schema
    // behavior is `additionalProperties: true`, so omitting matches that.
    if (n.additionalProperties === false) out.additionalProperties = false;
  } else if (n.type === 'array') {
    out.items = serializeNode(n.items ?? { type: 'any' });
  }
  return out;
}

// ─── Styles (shared with JsonObjectBuilder vibe) ────────────────────────────

const cardStyle: CSSProperties = {
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const rowWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: 'var(--sp-2)',
  borderBottom: '1px solid var(--border-subtle)',
};

const rowTop: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 96px auto auto',
  gap: 6,
  alignItems: 'center',
};

const smallBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-subtle)',
  color: 'var(--text-secondary)',
  borderRadius: 4,
  padding: '2px 8px',
  fontSize: 11,
  cursor: 'pointer',
  height: 26,
};

const monoInput: CSSProperties = {
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 12,
  padding: '4px 6px',
  height: 26,
};

// ─── Top-level component ────────────────────────────────────────────────────

export function JsonSchemaBuilder({
  value,
  onChange,
  readOnly = false,
}: {
  /** Raw JSON Schema document (or null = no contract). */
  value: Record<string, unknown> | null;
  onChange: (next: Record<string, unknown> | null) => void;
  /** When true, renders the builder UI without edit affordances (preview only). */
  readOnly?: boolean;
}) {
  const [mode, setMode] = useState<'builder' | 'raw'>('builder');
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState<string | null>(null);

  // Parsed AST — re-derived from `value` so external resets (server refetch)
  // flow through cleanly. The builder mutates via `setAst`, which serializes
  // back out through `onChange`.
  const ast = useMemo<ISchemaNode>(
    () => (value === null ? { type: 'object', fields: [] } : parseNode(value)),
    [value],
  );

  useEffect(() => {
    if (mode === 'raw') {
      setRawText(value === null ? '' : JSON.stringify(value, null, 2));
      setRawError(null);
    }
  }, [mode, value]);

  const setAst = (next: ISchemaNode) => {
    if (!readOnly) onChange(serializeNode(next));
  };

  return (
    <div
      style={{
        ...cardStyle,
        ...(readOnly ? { opacity: 0.85, pointerEvents: 'none' as const } : undefined),
      }}
      aria-readonly={readOnly || undefined}
    >
      {!readOnly ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px var(--sp-2)',
            borderBottom: '1px solid var(--border-subtle)',
          }}
        >
          <div style={{ display: 'flex', gap: 4 }}>
            <ModeToggle
              label="Builder"
              active={mode === 'builder'}
              onClick={() => setMode('builder')}
            />
            <ModeToggle label="Raw JSON" active={mode === 'raw'} onClick={() => setMode('raw')} />
          </div>
          <button
            type="button"
            style={{ ...smallBtn, borderColor: 'transparent' }}
            onClick={() => onChange(null)}
            title="Clear schema (no contract)"
          >
            Clear
          </button>
        </div>
      ) : null}

      {readOnly || mode === 'builder' ? (
        <div style={{ padding: 'var(--sp-2)' }}>
          <SchemaNodeEditor node={ast} onChange={setAst} root={true} readOnly={readOnly} />
        </div>
      ) : (
        <div style={{ padding: 'var(--sp-2)' }}>
          <textarea
            className="input"
            rows={14}
            style={{ ...monoInput, height: 'auto', width: '100%' }}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
          />
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-2)',
              alignItems: 'center',
              marginTop: 'var(--sp-2)',
            }}
          >
            <button
              type="button"
              style={smallBtn}
              onClick={() => {
                const trimmed = rawText.trim();
                if (trimmed === '') {
                  onChange(null);
                  setMode('builder');
                  setRawError(null);
                  return;
                }
                try {
                  const parsed = JSON.parse(trimmed) as unknown;
                  if (!isPlainObject(parsed)) throw new Error('Schema must be a JSON object.');
                  onChange(parsed);
                  setMode('builder');
                  setRawError(null);
                } catch (err) {
                  setRawError((err as Error).message);
                }
              }}
            >
              Apply
            </button>
            {rawError ? (
              <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                {rawError}
              </span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function ModeToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? 'var(--surface)' : 'transparent',
        color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
        border: '1px solid',
        borderColor: active ? 'var(--border-subtle)' : 'transparent',
        borderRadius: 4,
        padding: '2px 8px',
        fontSize: 11,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ─── Recursive editor for one schema node ───────────────────────────────────

function SchemaNodeEditor({
  node,
  onChange,
  root = false,
  readOnly = false,
}: {
  node: ISchemaNode;
  onChange: (next: ISchemaNode) => void;
  root?: boolean;
  readOnly?: boolean;
}) {
  if (node.type === 'object') {
    return <ObjectFieldsEditor node={node} onChange={onChange} root={root} readOnly={readOnly} />;
  }
  if (node.type === 'array') {
    return <ArrayItemsEditor node={node} onChange={onChange} readOnly={readOnly} />;
  }
  // Primitive / null / any leaf — at root we still want to let the user
  // promote it to an object. At a non-root location the type select on the
  // parent row handles that.
  if (root) {
    return (
      <div className="t-xs t-muted" style={{ padding: 'var(--sp-2)' }}>
        Root schema is <code>{node.type}</code>.
        {readOnly ? (
          <> Use the Agent editor for advanced schemas.</>
        ) : (
          <>
            {' '}
            Switch root type to <code>object</code> to add fields, or use Raw JSON for advanced
            schemas.
            <div style={{ marginTop: 'var(--sp-2)' }}>
              <button
                type="button"
                style={smallBtn}
                onClick={() => onChange({ type: 'object', fields: [] })}
              >
                Convert to object
              </button>
            </div>
          </>
        )}
      </div>
    );
  }
  return null;
}

function ObjectFieldsEditor({
  node,
  onChange,
  root,
  readOnly = false,
}: {
  node: ISchemaNode;
  onChange: (next: ISchemaNode) => void;
  root: boolean;
  readOnly?: boolean;
}) {
  const fields = node.fields ?? [];

  const updateField = (i: number, patch: Partial<IField>) => {
    const current = fields[i];
    if (!current) return;
    const next = fields.slice();
    next[i] = { ...current, ...patch };
    onChange({ ...node, fields: next });
  };
  const removeField = (i: number) =>
    onChange({ ...node, fields: fields.filter((_, j) => j !== i) });
  const addField = () => {
    // Seed the new field with a unique non-empty name. Round-tripping
    // through `serializeNode` drops fields with empty names (a JSON
    // Schema property can't be ""), so an empty-name row would vanish
    // as soon as the parent re-derives the AST from `value`. Picking
    // `field`, `field_2`, … keeps the row visible until the user
    // renames it.
    const taken = new Set(fields.map((f) => f.name));
    let name = 'field';
    for (let i = 2; taken.has(name); i++) name = `field_${i}`;
    onChange({
      ...node,
      fields: [...fields, { name, required: false, schema: { type: 'string' } }],
    });
  };

  const allowExtra = node.additionalProperties !== false;
  const toggleAllowExtra = (next: boolean) => onChange({ ...node, additionalProperties: next });

  return (
    <div style={root ? undefined : { ...cardStyle, background: 'var(--surface-2)' }}>
      {fields.length === 0 ? (
        <div style={{ padding: 'var(--sp-3)' }}>
          <div className="t-xs" style={{ color: 'var(--text-secondary)' }}>
            {readOnly ? 'No output fields declared.' : 'No fields yet — add one to start.'}
          </div>
          {!readOnly ? (
            <button
              type="button"
              style={{ ...smallBtn, marginTop: 'var(--sp-2)' }}
              onClick={addField}
            >
              + Add field
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {fields.map((f, i) => (
            <FieldRow
              key={i}
              field={f}
              siblings={fields.map((x) => x.name)}
              onChange={(patch) => updateField(i, patch)}
              onRemove={() => removeField(i)}
              readOnly={readOnly}
            />
          ))}
          {!readOnly ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--sp-2)',
                gap: 'var(--sp-2)',
              }}
            >
              <button type="button" style={smallBtn} onClick={addField}>
                + Add field
              </button>
              <label
                className="t-xs"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                }}
                title="When off, payloads with extra keys are rejected (additionalProperties: false)."
              >
                <input
                  type="checkbox"
                  checked={allowExtra}
                  onChange={(e) => toggleAllowExtra(e.target.checked)}
                />
                Allow additional properties
              </label>
            </div>
          ) : allowExtra === false ? (
            <div
              className="t-xs"
              style={{ color: 'var(--text-secondary)', padding: '0 var(--sp-2) var(--sp-2)' }}
            >
              Additional properties not allowed
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function FieldRow({
  field,
  siblings,
  onChange,
  onRemove,
  readOnly = false,
}: {
  field: IField;
  siblings: string[];
  onChange: (patch: Partial<IField>) => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const [nameDraft, setNameDraft] = useState(field.name);
  useEffect(() => setNameDraft(field.name), [field.name]);
  const duplicate = nameDraft !== field.name && siblings.filter((s) => s === nameDraft).length > 0;

  // For complex types (object/array), the row renders the nested editor
  // below the main row. Primitive types stay on a single row.
  const isComplex = field.schema.type === 'object' || field.schema.type === 'array';

  return (
    <div style={rowWrap}>
      <div
        style={{
          ...rowTop,
          gridTemplateColumns: readOnly ? 'minmax(0, 1fr) 96px auto' : rowTop.gridTemplateColumns,
        }}
      >
        <input
          className="input"
          value={nameDraft}
          readOnly={readOnly}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={() => {
            if (readOnly) return;
            if (duplicate) setNameDraft(field.name);
            else if (nameDraft !== field.name) onChange({ name: nameDraft });
          }}
          placeholder="field name"
          spellCheck={false}
          style={{ ...monoInput, minWidth: 0 }}
        />
        <select
          className="select-native"
          value={field.schema.type}
          disabled={readOnly}
          onChange={(e) =>
            onChange({
              schema: {
                ...defaultNode(e.target.value as SchemaType),
                description: field.schema.description,
              },
            })
          }
          style={{ fontSize: 12, height: 26, padding: '2px 4px', minWidth: 0 }}
        >
          {primitiveTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <label
          title="required"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            height: 26,
            fontSize: 10,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'var(--text-secondary)',
          }}
        >
          <input
            type="checkbox"
            checked={field.required}
            disabled={readOnly}
            onChange={(e) => onChange({ required: e.target.checked })}
          />
          req
        </label>
        {!readOnly ? (
          <button type="button" style={smallBtn} onClick={onRemove} title="Remove field">
            ×
          </button>
        ) : null}
      </div>
      <input
        className="input"
        value={field.schema.description ?? ''}
        readOnly={readOnly}
        onChange={(e) =>
          onChange({
            schema: {
              ...field.schema,
              description: e.target.value === '' ? undefined : e.target.value,
            },
          })
        }
        placeholder="description (optional)"
        style={{ fontSize: 12, padding: '4px 6px', height: 24, width: '100%' }}
      />
      {duplicate ? (
        <div className="t-xs" style={{ color: 'var(--status-danger)' }}>
          duplicate name
        </div>
      ) : null}
      {isComplex ? (
        <div style={{ marginTop: 4, paddingLeft: 'var(--sp-3)' }}>
          <SchemaNodeEditor
            node={field.schema}
            onChange={(nextSchema) => onChange({ schema: nextSchema })}
            readOnly={readOnly}
          />
        </div>
      ) : null}
    </div>
  );
}

function ArrayItemsEditor({
  node,
  onChange,
  readOnly = false,
}: {
  node: ISchemaNode;
  onChange: (next: ISchemaNode) => void;
  readOnly?: boolean;
}) {
  const items = node.items ?? { type: 'any' as SchemaType };
  return (
    <div style={{ ...cardStyle, background: 'var(--surface-2)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '60px 96px minmax(0, 1fr)',
          gap: 6,
          alignItems: 'center',
          padding: 'var(--sp-2)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <span className="t-xs" style={{ color: 'var(--text-secondary)' }}>
          items
        </span>
        <select
          className="select-native"
          value={items.type}
          disabled={readOnly}
          onChange={(e) =>
            onChange({
              ...node,
              items: {
                ...defaultNode(e.target.value as SchemaType),
                description: items.description,
              },
            })
          }
          style={{ fontSize: 12, height: 26, padding: '2px 4px' }}
        >
          {primitiveTypes.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <input
          className="input"
          value={items.description ?? ''}
          readOnly={readOnly}
          onChange={(e) =>
            onChange({
              ...node,
              items: {
                ...items,
                description: e.target.value === '' ? undefined : e.target.value,
              },
            })
          }
          placeholder="item description (optional)"
          style={{ fontSize: 12, padding: '4px 6px', height: 26 }}
        />
      </div>
      {items.type === 'object' || items.type === 'array' ? (
        <div style={{ padding: '0 var(--sp-2) var(--sp-2) var(--sp-4)' }}>
          <SchemaNodeEditor
            node={items}
            onChange={(nextItems) => onChange({ ...node, items: nextItems })}
            readOnly={readOnly}
          />
        </div>
      ) : null}
    </div>
  );
}
